<script setup lang="ts">
import { computed } from 'vue'

import type { SubParticipant } from '@collector/sportsapi-types'

import type {
  LineupConfig,
  PositionConfig,
} from '../../../../interfaces/LineupConfig'
import type { LineupModel } from '../../../../interfaces/LineupModel'
import { cloneWithKey } from '../../../../utils/model.utils'
import {
  cloneWithStats,
  isCaptain,
  setCaptain,
  unsetCaptain,
} from '../../../../utils/player.utils'
import SectionEmpty from '../SectionEmpty.vue'
import SectionTitle from '../SectionTitle.vue'
import PositionSectionItem from './PositionSectionItem.vue'
import SortableList from './SortableList.vue'

type Props = {
  positionKey: PropertyKey
  positionKeysMerged?: PropertyKey[]
  config: LineupConfig
}
const props = withDefaults(defineProps<Props>(), {
  positionKeysMerged: () => [],
})

// state
const lineup = defineModel<LineupModel>({ required: true })

const label = computed(
  () => props.config.positions[props.positionKey].sectionLabel,
)

const count = computed(() => {
  let count = lineup.value[props.positionKey]?.length ?? 0
  props.positionKeysMerged.forEach(
    (k) => (count += lineup.value[k]?.length ?? 0),
  )
  return count
})

// methods
function getEffectivePlayerConfig(
  key: PropertyKey,
  player: SubParticipant,
): PositionConfig {
  const base = props.config.positions[key]
  if (!base.getPlayerConfig) return base
  return { ...base, ...base.getPlayerConfig(player) }
}

function updatePlayer(
  key: PropertyKey,
  index: number,
  player: SubParticipant,
): void {
  const updatedLineup = cloneWithKey(lineup, key)
  updatedLineup[key].splice(index, 1, player)

  lineup.value = props.config.syncPositions
    ? props.config.syncPositions(updatedLineup, key, player)
    : updatedLineup
}

function deletePlayer(key: PropertyKey, index: number): void {
  const player = lineup.value[key][index]
  const updatedLineup = cloneWithKey(lineup, key)
  updatedLineup[key].splice(index, 1)

  lineup.value = props.config.syncPositions
    ? props.config.syncPositions(updatedLineup, key, player)
    : updatedLineup
}

function updateCaptain(newCaptain: SubParticipant): void {
  const updatedLineup = { ...lineup.value }
  for (const [positionKey, players] of Object.entries(updatedLineup)) {
    updatedLineup[positionKey] = players.map((player) => {
      if (player === newCaptain) {
        player = cloneWithStats(player)
        setCaptain(player)
      } else if (isCaptain(player)) {
        player = cloneWithStats(player)
        unsetCaptain(player)
      }
      return player
    })
  }
  lineup.value = updatedLineup
}

function deleteAll(): void {
  const updatedLineup = cloneWithKey(lineup)
  const keys = [props.positionKey, ...props.positionKeysMerged]
  keys.forEach((key) => (updatedLineup[key] = []))
  lineup.value = updatedLineup
}
</script>

<template>
  <div
    class="grid gap-1"
    data-testid="lineups-position-section"
  >
    <SectionTitle
      :label
      :count
      :showButton="config.positions[positionKey].showDeleteAll"
      buttonText="DELETE ALL"
      buttonConfirmText="Do you want to delete all players from this section?"
      @actionConfirmed="deleteAll"
    />
    <SectionEmpty
      v-if="count === 0"
      :label
    />
    <template
      v-for="key in [...positionKeysMerged, positionKey]"
      v-else
      :key
    >
      <SortableList
        v-model="lineup[key]"
        :disabled="!config.positions[positionKey].sortable"
      >
        <PositionSectionItem
          v-for="(player, i) in lineup[key]"
          :key="player.id || player.short_name"
          :lineupConfig="config"
          :config="getEffectivePlayerConfig(key, player)"
          :modelValue="player"
          :sortable="config.positions[positionKey].sortable"
          @update:modelValue="updatePlayer(key, i, $event)"
          @capitanUpdated="updateCaptain"
          @delete="deletePlayer(key, i)"
        />
      </SortableList>
    </template>
  </div>
</template>

<style scoped></style>
